import axios from 'axios';
import { getKeycloak } from './keycloak';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || '/api';

// Create axios instance with auth interceptor
const scenarioValidationClient = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
scenarioValidationClient.interceptors.request.use(async (config) => {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    config.headers.Authorization = `Bearer ${windowToken}`;
    return config;
  }

  // Fallback to Keycloak instance
  try {
    const keycloak = getKeycloak();
    if (keycloak?.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
  } catch (error) {
    console.warn('Could not get auth token for Scenario Validation API');
  }

  return config;
});

export interface ScenarioReference {
  type: string;
  key: string;
  uuid: string;
}

export interface SuggestedMatch {
  uuid: string;
  name: string;
  confidence: 'high' | 'medium' | 'low';
  matchReason: string;
}

export interface ReferenceValidationDetail {
  uuid: string;
  type: string;
  exists: boolean;
  resourceName?: string;
  usedBy?: string;  // Newline-separated names of VM Specs, Playbooks, Container Specs, etc. that use this resource
  message?: string;
  suggestion?: string;
  suggestedMatches?: SuggestedMatch[];
}

export interface ScenarioValidationReport {
  scenarioUuid: string;
  scenarioName: string;
  draftId: string;
  environmentId: string;
  environmentName: string;
  timestamp: string;
  overallValid: boolean;

  summary: {
    totalReferences: number;
    validReferences: number;
    missingReferences: number;
  };

  details: {
    byType: {
      [key: string]: {
        totalCount: number;
        uniqueCount: number;
        validCount: number;
        missingCount: number;
        references: ReferenceValidationDetail[];
      };
    };
  };
}

export const scenarioValidationService = {
  /**
   * Validate scenario drafts against a RangeOS environment
   */
  async validateScenarios(
    environmentId: string,
    draftIds: string[]
  ): Promise<ScenarioValidationReport[]> {
    const response = await scenarioValidationClient.post(
      `/rangeos-api/endpoints/${environmentId}/scenarios/validate`,
      { draftIds }
    );

    // Data is already properly serialized from the backend
    return response.data;
  },

  /**
   * Update draft references with new UUID mappings
   * @param options.transformRoleSchemas - If true, transform roleVariables to match target role schemas
   */
  async updateDraftReferences(
    environmentId: string,
    draftId: string,
    uuidMappings: { oldUuid: string; newUuid: string; type: string }[],
    options?: { transformRoleSchemas?: boolean }
  ): Promise<{ success: boolean; message: string; schemaTransformations?: number }> {
    const response = await scenarioValidationClient.post(
      `/rangeos-api/endpoints/${environmentId}/design-drafts/${draftId}/update-references`,
      { uuidMappings, transformRoleSchemas: options?.transformRoleSchemas }
    );
    return response.data;
  },

  /**
   * Publish an updated draft
   */
  async publishDraft(
    environmentId: string,
    draftId: string
  ): Promise<{ success: boolean; message: string; publishedId?: string }> {
    const response = await scenarioValidationClient.post(
      `/rangeos-api/endpoints/${environmentId}/design-drafts/${draftId}/publish`
    );
    return response.data;
  },

  /**
   * Validate a scenario bundle against target environment before importing
   */
  async validateScenarioBundle(
    endpointId: string,
    bundle: any
  ): Promise<ScenarioValidationReport> {
    const response = await scenarioValidationClient.post(
      `/rangeos-api/endpoints/${endpointId}/scenarios/validate-bundle`,
      { bundle }
    );
    return response.data;
  },

  /**
   * Validate a scenario bundle from S3 against target environment
   */
  async validateS3Bundle(
    endpointId: string,
    s3Key: string
  ): Promise<ScenarioValidationReport> {
    const response = await scenarioValidationClient.post(
      `/rangeos-api/endpoints/${endpointId}/scenarios/validate-s3-bundle`,
      { s3Key }
    );
    return response.data;
  },

  /**
   * Transform role schemas in a draft using migration rules
   * This is a standalone operation that doesn't require UUID mappings
   */
  async transformDraftSchemas(
    environmentId: string,
    draftId: string
  ): Promise<{ success: boolean; message: string; schemaTransformations: number; appliedRules: string[]; errors: string[] }> {
    const response = await scenarioValidationClient.post(
      `/rangeos-api/endpoints/${environmentId}/design-drafts/${draftId}/transform-schemas`
    );
    return response.data;
  },
};
